/**
 * Field Components Library
 *
 * Reusable field components for the page builder properties panel.
 * Each field type is a self-contained component that handles its own rendering and state.
 *
 * Usage:
 *   import { renderField, fieldComponents } from './fields';
 *
 *   // Option 1: Use renderField helper
 *   renderField(fieldConfig, value, onChange)
 *
 *   // Option 2: Use component directly
 *   const ColorField = fieldComponents.color;
 *   <ColorField field={fieldConfig} value={value} onChange={onChange} />
 */

// Field Components
import TextField from './TextField';
import TextareaField from './TextareaField';
import NumberField from './NumberField';
import ColorField from './ColorField';
import SelectField from './SelectField';
import CheckboxField from './CheckboxField';
import SpacingField from './SpacingField';
import DimensionField from './DimensionField';
import ResponsiveDimensionField from './ResponsiveDimensionField';
import ResponsiveDirectionField from './ResponsiveDirectionField';
import ResponsiveSpacingField from './ResponsiveSpacingField';
import ResponsiveVisibilityField from './ResponsiveVisibilityField';
import ResponsiveColumnLayoutField from './ResponsiveColumnLayoutField';
import ResponsiveSelectField from './ResponsiveSelectField';
import BorderField from './BorderField';
import FontFamilyField from './FontFamilyField';
import CustomCSSField from './CustomCSSField';
import AccordionItemsField from './AccordionItemsField';
import MediaField from './MediaField';
import LinkField from './LinkField';
import FlexChildField from './FlexChildField';
import FieldGroup from './FieldGroup';

// Compact layout components
import CompactField from './CompactField';
import IconToggleGroup from './IconToggleGroup';
import SectionDivider from './SectionDivider';

/**
 * Field component registry
 * Maps field type strings to their corresponding React components
 */
export const fieldComponents = {
  // Basic fields
  text: TextField,
  textarea: TextareaField,
  number: NumberField,
  color: ColorField,
  select: SelectField,
  checkbox: CheckboxField,

  // Spacing fields
  padding: SpacingField,
  margin: SpacingField,
  paddingFull: SpacingField,  // Backward compatibility alias
  marginFull: SpacingField,   // Backward compatibility alias

  // Dimension fields
  dimension: DimensionField,
  dimensionWithUnit: DimensionField,  // Backward compatibility alias
  responsiveDimension: ResponsiveDimensionField,
  responsiveDirection: ResponsiveDirectionField,

  // Responsive spacing fields
  responsivePadding: ResponsiveSpacingField,
  responsiveMargin: ResponsiveSpacingField,

  // Responsive visibility
  responsiveVisibility: ResponsiveVisibilityField,

  // Responsive column layout
  responsiveColumnLayout: ResponsiveColumnLayoutField,

  // Responsive select
  responsiveSelect: ResponsiveSelectField,

  // Style fields
  border: BorderField,
  fontFamily: FontFamilyField,
  customCSS: CustomCSSField,

  // Compact/toggle fields
  iconToggle: IconToggleGroup,

  // Complex item fields
  accordionItems: AccordionItemsField,

  // Media picker field
  media: MediaField,
  mediaPicker: MediaField,  // Alias

  // Link URL field (with internal page linking)
  link: LinkField,

  // Flex child field (sizing within flex parent)
  flexChild: FlexChildField,
};

/**
 * Renders a field component based on field configuration
 *
 * @param {Object} field - Field configuration object
 * @param {string} field.key - Unique identifier for the field
 * @param {string} field.type - Type of field (text, number, color, etc.)
 * @param {string} field.label - Display label
 * @param {any} field.default - Default value
 * @param {number} [field.min] - Minimum value (for number fields)
 * @param {number} [field.max] - Maximum value (for number fields)
 * @param {number} [field.step] - Step increment (for number fields)
 * @param {Array} [field.options] - Options for select fields
 * @param {any} value - Current field value
 * @param {Function} onChange - Callback when value changes
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.disabled] - Whether field is disabled
 * @param {string} [options.error] - Error message to display
 * @param {Object} [options.collapsedSections] - Map of collapsed section states
 * @param {Function} [options.toggleSection] - Function to toggle section collapse
 *
 * @returns {React.Element|null} Rendered field component or null if type not found
 */
export function renderField(field, value, onChange, options = {}) {
  const Component = fieldComponents[field.type];

  if (!Component) {
    console.warn(`Unknown field type: ${field.type}. Available types: ${Object.keys(fieldComponents).join(', ')}`);
    return null;
  }

  return (
    <Component
      key={field.key}
      field={field}
      value={value}
      onChange={onChange}
      {...options}
    />
  );
}

/**
 * Register a custom field component
 *
 * @param {string} type - Field type identifier
 * @param {React.Component} component - React component to render the field
 */
export function registerFieldComponent(type, component) {
  fieldComponents[type] = component;
}

/**
 * Get list of all available field types
 * @returns {string[]} Array of field type names
 */
export function getFieldTypes() {
  return Object.keys(fieldComponents);
}

// Export individual components for direct use
export {
  TextField,
  TextareaField,
  NumberField,
  ColorField,
  SelectField,
  CheckboxField,
  SpacingField,
  DimensionField,
  ResponsiveDimensionField,
  ResponsiveDirectionField,
  ResponsiveSpacingField,
  ResponsiveVisibilityField,
  ResponsiveColumnLayoutField,
  ResponsiveSelectField,
  BorderField,
  FontFamilyField,
  CustomCSSField,
  AccordionItemsField,
  MediaField,
  LinkField,
  FlexChildField,
  FieldGroup,
  // Compact layout components
  CompactField,
  IconToggleGroup,
  SectionDivider,
};

export default fieldComponents;
